import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@shared/hooks/useAuthStore';
import { getFirebaseAuth, isFirebaseConfigured } from '@shared/firebase';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
const tenantSlug = import.meta.env.VITE_TENANT_SLUG ?? 'teramo';

/**
 * Axios client con interceptor:
 * - Authorization: Bearer <firebase-token> (SEMPRE fresco, vedi resolveAuthToken)
 * - X-Tenant-ID: <slug> (da env)
 * - 401 → trigger logout (token realmente invalido / sessione revocata)
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantSlug,
  },
});

/**
 * T-89 — Token di accesso SEMPRE fresco.
 *
 * Prima l'interceptor mandava il token salvato UNA VOLTA al login (snapshot nello
 * store Zustand persistito): l'ID token Firebase vive ~1h, non veniva mai
 * rinnovato, quindi dopo la scadenza ogni richiesta faceva 401 → logout (la
 * sessione "da 30 minuti").
 *
 * `getIdToken()` ritorna il token cached se ancora valido, altrimenti lo RINNOVA
 * in modo trasparente usando il refresh token Firebase (lungo-vivente, persistito
 * localmente): così la sessione dura ben oltre le 24h SENZA ri-login, mentre
 * l'access token resta corto (1h) — nessuna costante allungata, nessun controllo
 * di sicurezza disattivato.
 *
 * Fallback allo store per dev-mock / Firebase non configurato (token `dev:...`).
 */
export async function resolveAuthToken(): Promise<string | null> {
  const stored = useAuthStore.getState().token;
  if (!isFirebaseConfigured() || import.meta.env.VITE_DEV_AUTH_MOCK === 'true') {
    return stored;
  }
  const user = getFirebaseAuth().currentUser;
  if (!user) return stored; // sessione Firebase non ancora ripristinata all'avvio
  try {
    return await user.getIdToken();
  } catch {
    // Refresh fallito (es. refresh token revocato): lascia il token vecchio,
    // il 401 successivo farà logout pulito.
    return stored;
  }
}

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await resolveAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  (error: AxiosError) => {
    if (error.response?.status === 401 && import.meta.env.VITE_DEV_AUTH_MOCK !== 'true') {
      useAuthStore.getState().logout();
    }
    return Promise.reject(error);
  },
);
